import { useNavigate } from 'react-router-dom';
import { User, Shield, Heart, Brain, Users, ChevronRight, ChevronLeft, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useIntakeForm } from '../hooks/useIntakeForm';
import { IntakeStepProgress } from '../components/intake/IntakeStepProgress';
import { IntakeSuccessView } from '../components/intake/IntakeSuccessView';
import { BasicInfoStep, InsuranceStep, MedicalHistoryStep, MentalHealthStep, ConsentStep } from '../components/intake/steps';

const STEPS = [
  { label: 'Basic Info', icon: User },
  { label: 'Insurance', icon: Shield },
  { label: 'Medical History', icon: Heart },
  { label: 'Mental Health', icon: Brain },
  { label: 'Family & Consent', icon: Users },
];

const IntakeForm = () => {
  const navigate = useNavigate();
  const {
    step,
    formData,
    submitting,
    submitted,
    update,
    updateEmergencyContact,
    canProceed,
    nextStep,
    prevStep,
    handleSubmit,
  } = useIntakeForm();

  if (submitted) {
    return <IntakeSuccessView onNavigateHome={() => navigate('/')} />;
  }

  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-warmBeige to-white py-8 px-4">
      <div className="container mx-auto max-w-3xl">
        {/* Header */}
        <div className="flex items-center mb-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-darkBrown hover:text-primaryOrange mr-4">
            <ArrowLeft size={20} className="mr-1" /> Home
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-darkBrown">New Patient Intake Form</h1>
            <p className="text-gray-500">Please complete all sections before your first appointment</p>
          </div>
        </div>

        <IntakeStepProgress steps={STEPS} currentStep={step} />

        {/* Form Card */}
        <Card className="shadow-2xl border-none" data-testid="intake-form-card">
          <CardContent className="p-8">
            {step === 0 && <BasicInfoStep formData={formData} update={update} updateEC={updateEmergencyContact} />}
            {step === 1 && <InsuranceStep formData={formData} update={update} />}
            {step === 2 && <MedicalHistoryStep formData={formData} update={update} />}
            {step === 3 && <MentalHealthStep formData={formData} update={update} />}
            {step === 4 && <ConsentStep formData={formData} update={update} />}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={step === 0}
                className="border-gray-300"
                data-testid="intake-prev-btn"
              >
                <ChevronLeft size={18} className="mr-1" /> Previous
              </Button>

              {isLastStep ? (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  className="bg-green-600 hover:bg-green-700 text-white px-8"
                  data-testid="intake-submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Intake Form'}
                </Button>
              ) : (
                <Button
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className="bg-primaryOrange hover:bg-accentOrange text-white"
                  data-testid="intake-next-btn"
                >
                  Next <ChevronRight size={18} className="ml-1" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntakeForm;
